/**
 * SettingsPane.tsx
 *
 * Two tiers, deliberately separated because "the search root" is ambiguous:
 *
 *  - **My search scope** — a personal view over the one shared index. Stored in
 *    Office roaming settings, applied as query params. Always shown.
 *  - **Index (admin)** — what the nightly crawl actually walks. Service-wide,
 *    stored server-side, and only rendered when /admin/settings says the caller
 *    may see it. Changing it affects everyone.
 *
 * Lives as a tab inside the taskpane rather than a second ribbon button: a new
 * entry point would mean editing both XML manifests and re-sideloading, and
 * this manifest has already been rejected once by schema validation.
 */
import { useEffect, useState } from "react";

import {
  useAdminSettings,
  useResetAdminSetting,
  useUpdateAdminSettings,
} from "../hooks/useAdminSettings";
import { useIndexStatus } from "../hooks/useProjects";
import type { ItemType } from "../api/types";
import type { UserSettings } from "../settings/useSettings";

interface SettingsPaneProps {
  settings: UserSettings;
  onChange: (patch: Partial<UserSettings>) => Promise<void>;
  isSaving: boolean;
  saveError: string | null;
}

const ITEM_TYPE_LABELS: Record<ItemType, string> = {
  email: "Email",
  rfi: "RFI",
  submittal: "Submittal",
};

function parseCsv(value: string): string[] {
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function SettingsPane({
  settings,
  onChange,
  isSaving,
  saveError,
}: SettingsPaneProps) {
  const indexStatus = useIndexStatus();
  const adminQuery = useAdminSettings();
  const isAdmin = adminQuery.isSuccess;

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-900">
            My search scope
          </h2>
          <p className="text-xs text-slate-500">
            Narrows what you see. Does not change what gets indexed.
          </p>
        </div>

        <CsvField
          id="scope-divisions"
          label="Divisions"
          hint="Comma-separated, e.g. 24, 32. Leave blank for all."
          value={settings.divisions.join(", ")}
          onCommit={(raw) => onChange({ divisions: parseCsv(raw) })}
        />

        <CsvField
          id="scope-years"
          label="Years"
          hint="Two-digit year folders, e.g. 25, 26. Leave blank for all."
          value={settings.years.join(", ")}
          onCommit={(raw) => onChange({ years: parseCsv(raw) })}
        />

        <CsvField
          id="scope-prefix"
          label="Folder"
          hint="Only show projects under this path. Leave blank for the whole index."
          placeholder="/Shared/BE/Projects/24"
          value={settings.pathPrefix ?? ""}
          onCommit={(raw) => onChange({ pathPrefix: raw.trim() || null })}
        />

        <div>
          <label
            htmlFor="default-item-type"
            className="mb-1 block text-sm font-medium text-slate-700"
          >
            Default item type
          </label>
          <select
            id="default-item-type"
            value={settings.defaultItemType}
            onChange={(event) =>
              void onChange({
                defaultItemType: event.target.value as ItemType,
              })
            }
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {(Object.keys(ITEM_TYPE_LABELS) as ItemType[]).map((type) => (
              <option key={type} value={type}>
                {ITEM_TYPE_LABELS[type]}
              </option>
            ))}
          </select>
        </div>

        <p className="text-xs text-slate-400" role="status">
          {isSaving ? "Saving…" : "Saved to your mailbox."}
        </p>
        {saveError && (
          <p role="alert" className="text-sm text-red-600">
            {saveError}
          </p>
        )}
      </section>

      <section className="flex flex-col gap-1 border-t border-slate-200 pt-4">
        <h2 className="text-sm font-semibold text-slate-900">Index</h2>
        {indexStatus.isSuccess ? (
          <dl className="text-xs text-slate-600">
            <div className="flex justify-between">
              <dt>Source</dt>
              <dd className="font-medium">
                {indexStatus.data.data_source === "demo"
                  ? "Sample data"
                  : indexStatus.data.data_source === "imported"
                    ? "Imported folder listing"
                    : "Egnyte"}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt>Projects</dt>
              <dd className="font-medium">
                {indexStatus.data.projects_active.toLocaleString()}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt>Last updated</dt>
              <dd className="font-medium">
                {indexStatus.data.last_indexed_at
                  ? new Date(
                      indexStatus.data.last_indexed_at,
                    ).toLocaleString()
                  : "never"}
              </dd>
            </div>
          </dl>
        ) : (
          <p className="text-xs text-slate-400">Index status unavailable.</p>
        )}
      </section>

      {isAdmin && <AdminSection />}
    </div>
  );
}

interface CsvFieldProps {
  id: string;
  label: string;
  hint: string;
  value: string;
  placeholder?: string;
  onCommit: (raw: string) => void | Promise<void>;
}

/**
 * A text input that commits on blur or Enter rather than on every keystroke —
 * each commit is a roaming-settings round-trip to the mailbox.
 */
function CsvField({
  id,
  label,
  hint,
  value,
  placeholder,
  onCommit,
}: CsvFieldProps) {
  const [draft, setDraft] = useState(value);

  useEffect(() => setDraft(value), [value]);

  return (
    <div>
      <label
        htmlFor={id}
        className="mb-1 block text-sm font-medium text-slate-700"
      >
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={draft}
        placeholder={placeholder}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => void onCommit(draft)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.currentTarget.blur();
          }
        }}
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      <p className="mt-1 text-xs text-slate-500">{hint}</p>
    </div>
  );
}

function AdminSection() {
  const adminQuery = useAdminSettings();
  const updateMutation = useUpdateAdminSettings();
  const resetMutation = useResetAdminSetting();

  const [rootDraft, setRootDraft] = useState("");
  const [divisionsDraft, setDivisionsDraft] = useState("");

  const settings = adminQuery.data;

  useEffect(() => {
    if (settings) {
      setRootDraft(settings.egnyte_root);
      setDivisionsDraft(settings.sync_divisions.join(", "));
    }
  }, [settings]);

  if (!settings) {
    return null;
  }

  const isDirty =
    rootDraft !== settings.egnyte_root ||
    divisionsDraft !== settings.sync_divisions.join(", ");

  return (
    <section className="flex flex-col gap-3 border-t border-slate-200 pt-4">
      <div>
        <h2 className="text-sm font-semibold text-slate-900">Index (admin)</h2>
        <p className="text-xs text-slate-500">
          Where the nightly crawl looks. Applies to everyone; takes effect on
          the next sync.
        </p>
      </div>

      <div>
        <label
          htmlFor="admin-root"
          className="mb-1 block text-sm font-medium text-slate-700"
        >
          Crawl root
        </label>
        <input
          id="admin-root"
          type="text"
          value={rootDraft}
          onChange={(event) => setRootDraft(event.target.value)}
          placeholder="/Shared/BE/Projects"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <p className="mt-1 text-xs text-slate-500">
          The Egnyte path the server requests over the API — not a mapped drive.
          A drive letter can&rsquo;t work here: the server is Linux and has no
          Z:. To index from a mapped drive instead, use the{" "}
          <code>import_projects</code> CLI.
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Default: {settings.defaults.egnyte_root} · backend:{" "}
          {settings.storage_backend}
        </p>
      </div>

      <div>
        <label
          htmlFor="admin-divisions"
          className="mb-1 block text-sm font-medium text-slate-700"
        >
          Divisions to crawl
        </label>
        <input
          id="admin-divisions"
          type="text"
          value={divisionsDraft}
          onChange={(event) => setDivisionsDraft(event.target.value)}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={!isDirty || updateMutation.isPending}
          onClick={() =>
            updateMutation.mutate({
              egnyte_root: rootDraft.trim(),
              sync_divisions: parseCsv(divisionsDraft),
            })
          }
          className="rounded-md bg-blue-700 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {updateMutation.isPending ? "Saving…" : "Save index settings"}
        </button>
        {settings.overridden.map((key) => (
          <button
            key={key}
            type="button"
            disabled={resetMutation.isPending}
            onClick={() => resetMutation.mutate(key)}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Reset {key.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      {updateMutation.isError && (
        <p role="alert" className="text-sm text-red-600">
          {updateMutation.error.message}
        </p>
      )}
      {updateMutation.isSuccess && !isDirty && (
        <p className="text-xs text-green-700">
          Saved. Run a sync to rebuild the index.
        </p>
      )}
    </section>
  );
}
